import { AfterViewInit, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@env';
import { FileUpload } from '@material';
import { translate } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs';

import { TableColumn } from '../../interface/table-column.interface';
import { TableData } from '../../interface/table-data.interface';
import { TableRecordAction } from '../../interface/table-record-action.interface';
import { RecordPayload } from '../../models/payload/recordpayload.class';
import { Record } from '../../models/record/record.class';
import { RecordField } from '../../models/record/recordfield.class';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { RecordsService } from '../../services/records/records.service';
import { UtilsService } from '../../services/utils/utils.service';
import { GetAssetWithLatestVersion } from '../../store/actions';
import { getAssetsWithLatestVersion$ } from '../../store/selectors';

@Component({
    selector: 'app-field-level-file-attachment',
    templateUrl: './field-level-file-attachment.component.html',
    styleUrls: ['./field-level-file-attachment.component.scss'],
    standalone: false,
})
export class FieldLevelFileAttachmentComponent implements OnInit, AfterViewInit {
    field: RecordField;
    record: Record;
    instanceId: string;
    files: FileList;
    fileName: File['name'];
    clearFiles = new BehaviorSubject(false);
    isFileTypeSupported = true;
    isMaxFileSizeAllowed = true;
    maxFileCountReached = false;
    fieldAttachmentData = {
        filename: '',
        url: '',
        title: translate('Browse'),
        return: 'true',
    };
    uploadedFiles: any[];
    allColumns: BehaviorSubject<TableColumn[]> = new BehaviorSubject([
        {
            name: translate('File Name'),
            type: 'text',
            icon: undefined,
            id: 'fileName',
            hideInAllColumnsList: false,
            hide: false,
            showCheckboxInHeader: false,
        },
        {
            name: translate('Link'),
            type: 'link',
            icon: undefined,
            id: 'url',
            hideInAllColumnsList: false,
            hide: false,
            showCheckboxInHeader: false,
        },
    ]);
    recordActions: TableRecordAction[] = [];
    selectedColumns = ['fileName', 'url'];
    tableData: BehaviorSubject<TableData[]> = new BehaviorSubject([]);
    filesListUpdated: BehaviorSubject<any>;
    getNewRecord: BehaviorSubject<boolean>;
    recordUpdateDisabled: boolean;
    inReadOnlyMode: boolean;
    service: any;
    uploading: boolean;
    public recordIds: string[];
    /** Records of a multi-record update; uploads go to each saved one and its field is kept in sync */
    public records: Record[];
    public parentRecordId: string;
    public unSubscribe = new Subject<void>();
    public actualAssetId: string;

    constructor(
        private dialog: MatDialog,
        protected _fileUploadService: FileUpload,
        protected _utils: UtilsService,
        private _records: RecordsService,
        private store$: Store
    ) {}

    public startUpload = () => {
        if (this.uploading) {
            return;
        }
        if (!this.records?.some(record => record.id) && this.record?.fields?.length > 1 && !this.record?.id) {
            return this._utils.alertError(translate('Please save the record before uploading attachments.'));
        }
        const maxUpload = this.field.attachment && this.field.attachment.maxFilesCount;
        const uploadedCount = this.uploadedFiles.length;
        if (maxUpload !== undefined && maxUpload <= uploadedCount) {
            return this._utils.alertError(translate('Maximum file upload count reached'));
        } else if (!this.files || this.files.length === 0) {
            return this._utils.alertError(translate('Select a file to upload'));
        }
        this.uploading = true;
        const promises = [];
        const parentRecordId = this.parentRecordId || this.record.parentId || '';
        const recordIds = this.records?.length ? this.records.filter(record => record.id).map(record => record.id) : this.recordIds;
        const replaced = (this.field.value as string[])?.length
            ? []
            : (this.records || [])
                  .filter(record => record.id)
                  .map(record => ({ record, fileUris: record.getFieldValue(this.field.id) as string[] }));
        if (recordIds) {
            recordIds.forEach(id => {
                promises.push(this.uploadFiles(id, parentRecordId));
            });
        } else {
            const recordId = this.record.id ? this.record.id : '';
            promises.push(this.uploadFiles(recordId, parentRecordId));
        }
        Promise.all(promises).then(recordsResponse => {
            this._utils.hideLoading();
            const response = recordsResponse[0];
            recordsResponse.forEach(({ records }) => {
                const field = this.records?.find(record => record.id === records?.recordId)?.getFieldByFieldId(this.field.id);
                field && Object.assign(field, { value: records.fileUris, originalValue: CommonUtilsService.cloneObject(records.fileUris) });
                field?.updateFiles();
            });
            if (!this.record.id) {
                this._utils.alertSuccess(translate('Record created. File has been uploaded'));
                this.pushValuesToRecordField(response && response.records);
                this.getNewRecord?.next(true);
            } else {
                this._utils.alertSuccess(translate('File has been uploaded'));
                this.pushValuesToRecordField(response && response.records);
            }
            this.files = undefined;
            this.fileName = '';
            this.uploading = false;
            replaced.forEach(({ record, fileUris }) =>
                this.removeFiles(record.id, record.getFieldByFieldId(this.field.id), fileUris || [])
            );
        });
    };

    private uploadFiles = (recordId: string, parentRecordId: string) => {
        return new Promise((resolve, reject) => {
            const url = `${environment.taxilla_api}/asset/attachments?instanceId=${this.instanceId}&entityRecordId=${encodeURIComponent(
                recordId
            )}&parentEntityRecordId=${encodeURIComponent(parentRecordId)}`;
            const service = this.service;
            this.record.id = recordId;
            this.record.assetDataId = this.instanceId;
            const recordpayload = new RecordPayload(this.record, true);
            recordpayload.fields = recordpayload.fields.filter(field => field.id === this.field.id);
            const fieldToPush = recordpayload.fields.find(field => field.id === this.field.id);
            if (!fieldToPush) {
                const record = this.records?.find(item => item.id === recordId);
                recordpayload.fields.push({ id: this.field.id, value: record ? record.getFieldValue(this.field.id) : this.field.value });
            } else {
                fieldToPush.value = fieldToPush.value || [];
            }
            this._fileUploadService.upload(
                this.files as any,
                {
                    filename: 'file',
                    entityRecordInfoVO: this._utils.convertJSONtoBlob(recordpayload),
                    attachmentDetail: this._utils.convertJSONtoBlob({
                        assetId: service.assetMetaUId || this.actualAssetId,
                        entityId: this.field.entityUid,
                        fieldId: this.field.id,
                        fileName: this.fileName,
                    }),
                },
                url,
                undefined,
                response => {
                    resolve(response);
                },
                response => {
                    this._utils.hideLoading();
                    this.uploading = false;
                    this._utils.alertError((response && response.msg) || translate('Failed to upload the file'));
                    reject(response);
                }
            );
        });
    };

    onNewValue = data => {
        this.files = data;
        if (this.files && this.field && this.field.attachment) {
            if (this.files[0].size > this.field.attachment.maxSize * 1024 * 1024) {
                this.isMaxFileSizeAllowed = false;
            } else {
                this.isMaxFileSizeAllowed = true;
            }
        }
    };

    onFileTypeSupportCheck = (data: boolean) => {
        this.isFileTypeSupported = data;
    };

    pushValuesToRecordField = ({ fileUris, recordId }: { fileUris: string[]; recordId: string }) => {
        if (!this.record.id) {
            this.record.id = recordId;
        }
        this.field.value = fileUris;
        this.field.originalValue = CommonUtilsService.cloneObject(fileUris);
        this.field.updateFiles();
        this.generateUploadedFiles();
    };

    closeModal = () => {
        this.dialog.closeAll();
    };

    generateUploadedFiles = () => {
        this.recordActions.splice(0);
        !this.inReadOnlyMode &&
            !this.recordUpdateDisabled &&
            this.recordActions.push({
                displayName: translate('Delete'),
                iconName: 'delete',
                onActionTrigger: this.deleteFile,
            });
        this.uploadedFiles = [];
        this.field &&
            this.field.value &&
            Array.isArray(this.field.value) &&
            this.field.value.forEach(uploadedFileRecordUri => {
                const name = uploadedFileRecordUri.substring(uploadedFileRecordUri.lastIndexOf('/') + 1, uploadedFileRecordUri.length);
                this.uploadedFiles.push({
                    name,
                    fileUrl: environment.taxilla_api + '/asset/attachments?fileUri=' + uploadedFileRecordUri,
                    originalUrl: uploadedFileRecordUri,
                });
            });
        if (this.field && this.field.attachment && this.field.attachment.maxFilesCount) {
            if (this.uploadedFiles.length >= this.field.attachment.maxFilesCount) {
                this.maxFileCountReached = true;
            } else {
                this.maxFileCountReached = false;
            }
        }
        this.transformData();
    };

    /** Deletes the file from the record, or the same-named file from every saved record in a multi-record update */
    deleteFile = (file: { recordId: string; fileName: string; url: string; originalUrl: string }) => {
        const name = file.originalUrl.substring(file.originalUrl.lastIndexOf('/') + 1);
        if (!this.records) return this.removeFiles(this.record.id, this.field, [file.originalUrl]);
        this.records
            .filter(record => record.id)
            .forEach(record =>
                this.removeFiles(
                    record.id,
                    record.getFieldByFieldId(this.field.id),
                    ((record.getFieldValue(this.field.id) as string[]) || []).filter(uri => uri.endsWith('/' + name))
                )
            );
    };

    /** Deletes the files from the record one after another, keeping its field and the dialog's file list in sync */
    private removeFiles = (recordId: string, recordField: RecordField, [fileUri, ...rest]: string[]) =>
        fileUri &&
        this._records.deleteFieldAttachment(
            {
                assetDataId: this.instanceId,
                assetId: this.service.assetMetaUId || this.service.id || this.actualAssetId,
                entityId: this.field.entityUid,
                fieldId: this.field.id,
                fileUri,
                parentRecordId: this.parentRecordId,
                recordId,
            },
            {
                successCallback: () => {
                    [recordField, this.field].forEach(field => {
                        field.value = (field.value as string[]).filter(uri => uri !== fileUri);
                        field.originalValue = CommonUtilsService.cloneObject(field.value);
                        field.updateFiles();
                    });
                    this.generateUploadedFiles();
                    this.removeFiles(recordId, recordField, rest);
                },
                failureCallback: response => {
                    this._utils.alertError(response?.msg || translate('Failed to delete attachment'));
                },
            }
        );

    transformData = () => {
        const tableData: TableData[] = [];
        this.uploadedFiles.forEach((file, index) => {
            tableData.push({
                recordId: index.toString(),
                fileName: file.name,
                url: file.fileUrl,
                originalUrl: file.originalUrl,
            });
        });
        this.tableData.next(tableData);
        if (this.tableData?.value?.length === this.field?.attachment?.maxFilesCount) {
            this.maxFileCountReached = true;
        }
    };

    ngOnInit() {
        this.store$.dispatch(GetAssetWithLatestVersion({ serviceId: this.service.serviceId }));
        this.store$
            .select(getAssetsWithLatestVersion$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined)
            )
            .subscribe(data => {
                this.actualAssetId = data?.[this.service?.serviceId]?.metadataId;
            });
    }

    ngOnDestroy(): void {
        this.unSubscribe.next();
        this.unSubscribe.complete();
    }

    ngAfterViewInit(): void {}
}
